// frontend/src/components/TradingRecommendations.tsx

import React from 'react';
import { Card, Divider, Row, Col, Tag, Alert, Typography } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  PauseOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  TagOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { DetailedRecommendations, TradingRecommendation } from '../types';

const { Title } = Typography;

interface Props {
  recommendations: DetailedRecommendations;
}

const TradingRecommendations: React.FC<Props> = ({ recommendations }) => {
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'buy':
        return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
      case 'sell':
        return <ArrowDownOutlined style={{ color: '#f5222d' }} />;
      default:
        return <PauseOutlined style={{ color: '#fa8c16' }} />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'buy':
        return 'success';
      case 'sell':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low':
        return 'green';
      case 'medium':
        return 'orange';
      default:
        return 'red';
    }
  };

  const renderRecommendation = (rec: TradingRecommendation, isImmediate: boolean = false) => (
    <Card
      key={`${rec.action}-${rec.time}-${rec.confidenceScore}`}
      size="small"
      style={{
        marginBottom: 16,
        border: isImmediate ? '2px solid #1890ff' : undefined,
        boxShadow: isImmediate ? '0 2px 8px rgba(24,144,255,0.2)' : undefined,
      }}
    >
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {getActionIcon(rec.action)}
              <Tag color={getActionColor(rec.action)}>
                {rec.action === 'buy' ? '買入' : rec.action === 'sell' ? '賣出' : '持有'}
              </Tag>
              <Tag icon={<ClockCircleOutlined />} color="blue">
                {rec.time}
              </Tag>
              <Tag icon={<WarningOutlined />} color={getRiskColor(rec.riskLevel)}>
                {rec.riskLevel === 'low' ? '低風險' : rec.riskLevel === 'medium' ? '中風險' : '高風險'}
              </Tag>
            </div>
            <div style={{ fontWeight: 'bold' }}>信心度: {rec.confidenceScore}%</div>
          </div>
        </Col>

        {rec.action !== 'hold' && (
          <Col span={24}>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <DollarOutlined />
                  <div style={{ fontSize: 12, color: '#666' }}>建議金額</div>
                  <div style={{ fontWeight: 'bold' }}>${rec.amount.toLocaleString()}</div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <TagOutlined />
                  <div style={{ fontSize: 12, color: '#666' }}>價格區間</div>
                  <div style={{ fontWeight: 'bold' }}>
                    ${rec.priceRange.min.toFixed(2)} - ${rec.priceRange.max.toFixed(2)}
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {rec.action === 'buy' ? '目標收益' : '風險控制'}
                  </div>
                  <div style={{ fontWeight: 'bold', color: rec.action === 'buy' ? '#52c41a' : '#f5222d' }}>
                    {(((rec.priceRange.max - rec.priceRange.min) / rec.priceRange.min) * 100).toFixed(2)}%
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        )}

        <Col span={24}>
          <div
            style={{
              padding: 8,
              backgroundColor: '#f5f5f5',
              borderRadius: 4,
              fontSize: 13,
            }}
          >
            <strong>AI推理：</strong>
            {rec.reasoning}
          </div>

          {rec.action !== 'hold' && (
            <>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                預估手續費：{(rec.amount * 0.001).toFixed(2)} USDT
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>
                <span>
                  止損價：{typeof rec.stopLoss === 'number' ? `$${rec.stopLoss.toFixed(2)}` : 'N/A'}
                </span>
                <span style={{ margin: '0 12px' }}>
                  止盈價：{typeof rec.takeProfit === 'number' ? `$${rec.takeProfit.toFixed(2)}` : 'N/A'}
                </span>
              </div>
            </>
          )}
        </Col>
      </Row>
    </Card>
  );

  return (
    <div>
      <Title level={4}>🤖 AI 交易建議</Title>
      <Card style={{ marginBottom: 24 }}>
        <h4 style={{ color: '#1890ff' }}>⚡ 立即執行建議</h4>
        {recommendations.immediate.map((rec) => renderRecommendation(rec, true))}

        {recommendations.shortTerm.length > 0 && (
          <>
            <h4 style={{ color: '#fa8c16', marginTop: 16 }}>📅 短期計劃建議</h4>
            {recommendations.shortTerm.map((rec) => renderRecommendation(rec))}
          </>
        )}

        <Divider />

        <Row gutter={16}>
          <Col span={8}>
            <Card size="small" title="📊 AI 推理過程">
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{recommendations.reasoning}</div>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" title="🔮 市場展望">
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{recommendations.marketOutlook}</div>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" title="⚠️ 風險評估">
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{recommendations.riskAssessment}</div>
            </Card>
          </Col>
        </Row>

        <Alert
          message="重要提醒"
          description="以上建議僅供參考，請結合自身風險承受能力謹慎投資。"
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Card>
    </div>
  );
};

export default TradingRecommendations;
